/**
 * Position a coaching card relative to a target rect, honoring a
 * preferred placement and gracefully falling back when there's no room.
 *
 * Pure function — no React, no DOM. The caller supplies the viewport
 * size; we return absolute (left, top) for fixed positioning.
 */

import type { CardPlacement } from "../editor/steps/StepDefinition";

export interface PlacementInput {
  target: { x: number; y: number; w: number; h: number };
  card: { w: number; h: number };
  viewport: { w: number; h: number };
  preference: CardPlacement;
  /** Gap between target and card in px. */
  gap?: number;
  /** Min distance the card must keep from any viewport edge. */
  margin?: number;
}

export interface PlacementResult {
  left: number;
  top: number;
  side: "above" | "below" | "left" | "right" | "modal";
}

const DEFAULT_GAP = 12;
const DEFAULT_MARGIN = 12;

export function placeCard(input: PlacementInput): PlacementResult {
  const {
    target,
    card,
    viewport,
    preference,
    gap = DEFAULT_GAP,
    margin = DEFAULT_MARGIN,
  } = input;

  if (preference === "modal-center") {
    return {
      left: Math.max(margin, (viewport.w - card.w) / 2),
      top: Math.max(margin, (viewport.h - card.h) / 2),
      side: "modal",
    };
  }

  if (preference === "anchor-canvas-top") {
    // Anchor the card to the top center of the canvas area (target),
    // staying inside the viewport.
    const left = clamp(
      target.x + target.w / 2 - card.w / 2,
      margin,
      viewport.w - card.w - margin,
    );
    const top = clamp(target.y + gap, margin, viewport.h - card.h - margin);
    return { left, top, side: "below" };
  }

  const trials: Array<PlacementResult["side"]> = orderedSides(preference);

  for (const side of trials) {
    const candidate = tryPlace(side, target, card, viewport, gap, margin);
    if (candidate) return candidate;
  }

  // Last resort: pin to top-right corner of the viewport so the card
  // is still reachable.
  return {
    left: viewport.w - card.w - margin,
    top: margin,
    side: "below",
  };
}

function orderedSides(
  preference: CardPlacement,
): Array<PlacementResult["side"]> {
  switch (preference) {
    case "below-target":
      return ["below", "above", "right", "left"];
    case "above-target":
      return ["above", "below", "right", "left"];
    case "right-of-target":
      return ["right", "below", "above", "left"];
    case "left-of-target":
      return ["left", "below", "above", "right"];
    default:
      return ["below", "above", "right", "left"];
  }
}

function tryPlace(
  side: PlacementResult["side"],
  t: PlacementInput["target"],
  c: PlacementInput["card"],
  v: PlacementInput["viewport"],
  gap: number,
  margin: number,
): PlacementResult | null {
  if (side === "below") {
    const top = t.y + t.h + gap;
    if (top + c.h > v.h - margin) return null;
    const left = clamp(t.x + t.w / 2 - c.w / 2, margin, v.w - c.w - margin);
    return { left, top, side };
  }
  if (side === "above") {
    const top = t.y - c.h - gap;
    if (top < margin) return null;
    const left = clamp(t.x + t.w / 2 - c.w / 2, margin, v.w - c.w - margin);
    return { left, top, side };
  }
  if (side === "right") {
    const left = t.x + t.w + gap;
    if (left + c.w > v.w - margin) return null;
    const top = clamp(t.y + t.h / 2 - c.h / 2, margin, v.h - c.h - margin);
    return { left, top, side };
  }
  // left
  const left = t.x - c.w - gap;
  if (left < margin) return null;
  const top = clamp(t.y + t.h / 2 - c.h / 2, margin, v.h - c.h - margin);
  return { left, top, side };
}

function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}
